import { type ClientContext, ContextClient } from '~/services/contextClient';
import OutboundMessagesAnalyticsClientFactory from './outboundMessagesAnalyticsClient.factory';
import { analyticsClientConfig, type AnalyticsDateRangeParam } from '../analyticsClient.types';
import type {
    ClickThroughRateAnalyticsParams,
    OutboundMessageFilters,
    OutboundMessagesParams,
    OutboundMessagesAnalyticsClientInterface,
} from './outboundMessagesAnalyticsClient.types';
import type { ExtendedAxiosRequestConfig } from '~/services/BaseClient';

class OutboundMessagesAnalyticsClient extends ContextClient<OutboundMessagesAnalyticsClientInterface> {
    constructor() {
        super({
            createClient: (context: ClientContext) =>
                OutboundMessagesAnalyticsClientFactory.createClient(context, analyticsClientConfig),
        });
    }

    getTotalMessagesSentByAudienceType(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getTotalMessagesSentByAudienceType(params);
    }

    getTotalMessagesSentByResource(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getTotalMessagesSentByResource(params);
    }

    getOptOutRate(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getOptOutRate(params);
    }

    getClickThroughRate(context: ClientContext, params: ClickThroughRateAnalyticsParams) {
        const client = this.getClient(context);
        return client.getClickThroughRate(params);
    }

    getUniqueClickThroughRate(context: ClientContext, params: ClickThroughRateAnalyticsParams) {
        const client = this.getClient(context);
        return client.getUniqueClickThroughRate(params);
    }

    getMessageReplyRate(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getMessageReplyRate(params);
    }

    getListActiveResources(
        context: ClientContext,
        params: AnalyticsDateRangeParam & Pick<OutboundMessageFilters['filters'], 'resources'>,
        config?: ExtendedAxiosRequestConfig
    ) {
        const client = this.getClient(context);
        return client.getListActiveResources(params, config);
    }

    getTotalActiveResourcesByResourceType(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getTotalActiveResourcesByResourceType(params);
    }

    getTotalMessagesSentByMessageType(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getTotalMessagesSentByMessageType(params);
    }

    getDeliveryRate(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getDeliveryRate(params);
    }

    getTotalContactsMessaged(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getTotalContactsMessaged(params);
    }

    getClickThroughRateByResource(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getClickThroughRateByResource(params);
    }

    getUniqueClickThroughRateByResource(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getUniqueClickThroughRateByResource(params);
    }

    getReplyRateByResource(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getReplyRateByResource(params);
    }

    getOptOutRateByResource(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getOptOutRateByResource(params);
    }

    getExportResourceMetrics(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getExportResourceMetrics(params);
    }

    getExportAudienceOriginMetrics(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        return client.getExportAudienceOriginMetrics(params);
    }

    getCategoryMetricGroupPlot(
        context: ClientContext,
        category: string,
        metric: string,
        params: OutboundMessagesParams
    ) {
        const client = this.getClient(context);
        if (client.getCategoryMetricGroupPlot) {
            return client.getCategoryMetricGroupPlot(category, metric, params);
        }
        throw new Error(`Category metric group plot is not supported by ${client.constructor.name}`);
    }

    getCategoryMetricNotableMessages(
        context: ClientContext,
        category: string,
        metric: string,
        params: OutboundMessagesParams
    ) {
        const client = this.getClient(context);
        if (client.getCategoryMetricNotableMessages) {
            return client.getCategoryMetricNotableMessages(category, metric, params);
        }
        throw new Error(`Category metric notable messages is not supported by ${client.constructor.name}`);
    }

    getExportGroupMetrics(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        if (client.getExportGroupMetrics) {
            return client.getExportGroupMetrics(params);
        }
        throw new Error(`Export group metrics is not supported by ${client.constructor.name}`);
    }

    getTotalMessagesSentByResourceType(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        if (client.getTotalMessagesSentByResourceType) {
            return client.getTotalMessagesSentByResourceType(params);
        }
        throw new Error(`Total messages sent by resource type is not supported by ${client.constructor.name}`);
    }

    getTotalActiveGroups(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        if (client.getTotalActiveGroups) {
            return client.getTotalActiveGroups(params);
        }
        throw new Error(`Total active groups is not supported by ${client.constructor.name}`);
    }

    getGroupPerformanceByCategory(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        if (client.getGroupPerformanceByCategory) {
            return client.getGroupPerformanceByCategory(params);
        }
        throw new Error(`Group performance by category is not supported by ${client.constructor.name}`);
    }

    getCategoryPerformanceTargets(context: ClientContext) {
        const client = this.getClient(context);
        if (client.getCategoryPerformanceTargets) {
            return client.getCategoryPerformanceTargets();
        }
        throw new Error(`Category performance targets is not supported by ${client.constructor.name}`);
    }

    setCategoryPerformanceTargets(context: ClientContext, params: OutboundMessagesParams) {
        const client = this.getClient(context);
        if (client.setCategoryPerformanceTargets) {
            return client.setCategoryPerformanceTargets(params);
        }
        throw new Error(`Set category performance targets is not supported by ${client.constructor.name}`);
    }
}

export default new OutboundMessagesAnalyticsClient();
